/**
 * SharedListPage
 *
 * Public page for viewing a shared list of places.
 * Supports password protection.
 */

import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  Spin,
  Alert,
  Typography,
  Empty,
  Result,
} from 'antd';
import { LockOutlined, EyeOutlined, LinkOutlined } from '@ant-design/icons';
import {
  getSharedListInfo,
  verifySharedListPassword,
  getSharedListPlaces,
} from '@/services/sharedListApi';
import type { SharedListInfo, SharedListPlacesResponse } from '@/types/sharedList';
import type { PlaceListItem } from '@/types/place';
import type { ColumnsType } from 'antd/es/table';

const { Title, Paragraph, Text } = Typography;

/**
 * Get table columns based on visible columns configuration
 */
function getTableColumns(visibleColumns: string[]): ColumnsType<PlaceListItem> {
  const allColumns: Record<string, ColumnsType<PlaceListItem>[number]> = {
    name: {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 200,
      render: (name: string, record: PlaceListItem) => (
        <a
          href={record.googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-google-place-id={record.placeId}
        >
          {name}
        </a>
      ),
    },
    category: {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 150,
    },
    rating: {
      title: 'Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 80,
      render: (rating: number | null) => (rating ? `${rating.toFixed(1)}` : '-'),
    },
    reviewCount: {
      title: 'Reviews',
      dataIndex: 'reviewCount',
      key: 'reviewCount',
      width: 80,
      render: (count: number | null) => (count ? count.toLocaleString() : '-'),
    },
    address: {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: 250,
      ellipsis: true,
    },
    phone: {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
      render: (phone: string | null) =>
        phone ? (
          <a href={`tel:${phone}`}>{phone}</a>
        ) : (
          '-'
        ),
    },
    website: {
      title: 'Website',
      dataIndex: 'website',
      key: 'website',
      width: 150,
      ellipsis: true,
      render: (website: string | null) =>
        website ? (
          <a href={website} target="_blank" rel="noopener noreferrer">
            <LinkOutlined /> Visit
          </a>
        ) : (
          '-'
        ),
    },
    priceLevel: {
      title: 'Price',
      dataIndex: 'priceLevel',
      key: 'priceLevel',
      width: 80,
    },
    coordinates: {
      title: 'Coordinates',
      key: 'coordinates',
      width: 200,
      render: (_: unknown, record: PlaceListItem) =>
        record.latitude && record.longitude
          ? `${record.latitude.toFixed(6)}, ${record.longitude.toFixed(6)}`
          : '-',
    },
  };

  return visibleColumns
    .filter((col) => allColumns[col])
    .map((col) => allColumns[col]!);
}

export function SharedListPage() {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [listInfo, setListInfo] = useState<SharedListInfo | null>(null);
  const [data, setData] = useState<SharedListPlacesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Fetch list info
  useEffect(() => {
    if (!slug) return;

    const fetchInfo = async () => {
      try {
        setLoading(true);
        const info = await getSharedListInfo(slug);
        setListInfo(info);

        // If no password required, fetch places immediately
        if (!info.hasPassword) {
          setAuthenticated(true);
          await fetchPlaces();
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError('This shared list was not found.');
        } else if (err.response?.status === 410) {
          setError('This shared list has expired.');
        } else {
          setError('Failed to load shared list.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInfo();
  }, [slug]);

  // Fetch places
  const fetchPlaces = async (pwd?: string) => {
    if (!slug) return;

    try {
      setLoading(true);
      const response = await getSharedListPlaces(slug, {
        password: pwd || password,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setData(response);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setAuthenticated(false);
        setError('Invalid password');
      } else {
        setError('Failed to load places.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Verify password
  const handleVerifyPassword = async () => {
    if (!slug || !password) return;

    setVerifying(true);
    try {
      const valid = await verifySharedListPassword(slug, password);
      if (valid) {
        setAuthenticated(true);
        setError(null);
        await fetchPlaces(password);
      } else {
        setError('Invalid password');
      }
    } catch {
      setError('Failed to verify password');
    } finally {
      setVerifying(false);
    }
  };

  // Refetch when pagination changes
  useEffect(() => {
    if (authenticated && slug) {
      fetchPlaces();
    }
  }, [page, pageSize]);

  // Error state
  if (error && !listInfo) {
    return (
      <div style={{ padding: 40, maxWidth: 800, margin: '0 auto' }}>
        <Result
          status="error"
          title="Unable to load shared list"
          subTitle={error}
        />
      </div>
    );
  }

  // Loading state
  if (loading && !listInfo) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin size="large" />
        <Paragraph style={{ marginTop: 16 }}>Loading shared list...</Paragraph>
      </div>
    );
  }

  // Password required
  if (listInfo?.hasPassword && !authenticated) {
    return (
      <div style={{ padding: 40, maxWidth: 400, margin: '0 auto' }}>
        <Card>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <LockOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
            <Title level={4}>{listInfo.name || 'Shared List'}</Title>
            <Paragraph type="secondary">
              This shared list is password protected.
            </Paragraph>
          </div>

          {error && <Alert message={error} type="error" style={{ marginBottom: 16 }} />}

          <Space direction="vertical" style={{ width: '100%' }}>
            <Input.Password
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onPressEnter={handleVerifyPassword}
              prefix={<LockOutlined />}
            />
            <Button
              type="primary"
              block
              onClick={handleVerifyPassword}
              loading={verifying}
              disabled={!password}
            >
              Access List
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  // Show places
  const columns = getTableColumns(data?.list.visibleColumns || listInfo?.visibleColumns || []);

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Title level={3} style={{ marginBottom: 8 }}>
                {data?.list.name || listInfo?.name || 'Shared Places'}
              </Title>
              {(data?.list.description || listInfo?.description) && (
                <Paragraph type="secondary">
                  {data?.list.description || listInfo?.description}
                </Paragraph>
              )}
            </div>
            <Space>
              <EyeOutlined />
              <Text type="secondary">
                {data?.pagination.total.toLocaleString() || 0} places
              </Text>
            </Space>
          </div>
        </Space>
      </Card>

      <Card>
        {data?.places.length === 0 ? (
          <Empty description="No places in this list" />
        ) : (
          <Table
            columns={columns}
            dataSource={data?.places || []}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize,
              total: data?.pagination.total || 0,
              onChange: (newPage, newPageSize) => {
                setPage(newPage);
                setPageSize(newPageSize);
              },
              showSizeChanger: true,
              showTotal: (total) => `Total ${total.toLocaleString()} places`,
            }}
          />
        )}
      </Card>
    </div>
  );
}

export default SharedListPage;
